// E2E-POLL (2026-10-21): app.js waits for the full version of a draft (two stages, docs/PROCESSING.md v2). Before, it asked
// Supabase every 12 s (ensureWatch). When the sync engine runs (api/sync.ts mode read or full), the same change already
// reaches this tab as a Realtime push on sync_events (core/sync/adapters/supabase-realtime.ts) and lands in the engine's
// local list and jobs; this file tells app.js when one of the meetings it watches changed there, so app.js reads that
// meeting once instead of on a timer. The engine fills gaps itself (reconnect, back online, its 5 minute pull).
// Resolves to null when the engine is off or does not start: app.js then keeps its poll, so a server without
// schema-next (production today) behaves as before.
import type { Host } from "./host";
import { modeOf } from "../api/sync-mode";

export type WatchHost = Pick<Host, "sb" | "session" | "legacy">;
type Row = { id?: unknown; notes_version?: unknown; refine?: { status?: unknown } | null };

/** calls onChange(ids) with the watched meetings whose version or job state changed in the engine; returns the stop */
export async function watchNotes(
  host: WatchHost,
  watched: () => string[],
  onChange: (ids: string[]) => void
): Promise<(() => void) | null> {
  if (modeOf(host as Host) === "off") return null;
  // the engine (Dexie, IndexedDB) loads only here and in api/sync.ts, never with the first page
  const [{ ensureSync, peekList }, { LIST_SPEC, JOBS_SPEC }] = await Promise.all([import("../api/sync"), import("../core/sync/engine")]);
  const engine = await ensureSync(host as Host).catch(() => null);
  if (!engine) return null;
  const seen = new Map<string, string>();
  const sig = (r: Row | undefined) => `${String(r?.notes_version ?? "")}:${String(r?.refine?.status ?? "")}`;
  const look = () => {
    const ids = watched();
    if (!ids.length) return;
    const rows = new Map(((peekList() ?? []) as Row[]).map(r => [String(r.id), r]));
    const changed = ids.filter(id => {
      const s = sig(rows.get(id));
      const before = seen.get(id);
      seen.set(id, s);
      return before !== undefined && before !== s;
    });
    if (changed.length) onChange(changed);
  };
  look(); // what the engine holds now is the starting point
  const offs = [engine.query(LIST_SPEC).subscribe(look), engine.query(JOBS_SPEC).subscribe(look)];
  return () => offs.forEach(off => off());
}
